import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type ThemeName = "light" | "dark";
type ThemePreference = ThemeName | "system";

const defaultPreference: ThemePreference = "system";

const ThemeContext = createContext({
  themePreference: defaultPreference as ThemePreference,
  isDarkMode: false,
  setThemePreference: (_theme: ThemePreference) => {},
  toggleTheme: () => {},
});

function getInitialPreference(): ThemePreference {
  const stored = localStorage.theme as ThemePreference | undefined;
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return defaultPreference;
}

function getSystemTheme(): ThemeName {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themePreference, setThemePreference] = useState<ThemePreference>(
    getInitialPreference(),
  );

  const currentTheme =
    themePreference === "system" ? getSystemTheme() : themePreference;

  useEffect(() => {
    const root = document.documentElement;

    function cb() {
      root.classList.remove("dark", "light");
      root.classList.add(currentTheme);
    }

    // Fallback: if View Transition API isn't supported, just switch instantly
    if (!document.startViewTransition) {
      cb();
    } else {
      document.startViewTransition(cb);
    }

    localStorage.theme = themePreference;
  }, [themePreference, currentTheme]);

  useEffect(() => {
    if (themePreference !== "system") {
      return;
    }
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const theme = getSystemTheme();
      const root = document.documentElement;
      root.classList.remove("dark", "light");
      root.classList.add(theme);
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [themePreference]);

  const toggleTheme = useCallback(() => {
    setThemePreference((prev) => {
      if (prev === "system") return "light";
      if (prev === "light") return "dark";
      return "system";
    });
  }, []);

  const themeValue = useMemo(
    () => ({
      themePreference,
      isDarkMode: currentTheme === "dark",
      setThemePreference,
      toggleTheme,
    }),
    [themePreference, currentTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={themeValue}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
